import { Banner, DataGrid, PaginationParams, useDialog } from '@if/mui'
import { usePagedCommandHistory } from '@src/api/dev-tools-history-api'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCommandHistoryColumns } from './CommandHistoryColumns'

export interface CommandHistoryDialogProps {
  commandType: DevToolsCommandType
  commandTitle: string
}

const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [10, 25, 50]

/**
 * The dialog's own state. It runs inside the open dialog, once per instance, so the pager
 * in the body and the query behind it stay in step — a `useState` at the declaration site
 * cannot drive a dialog slot. Module scope keeps the reference stable.
 *
 * The endpoint returns a page and no total, which is exactly what the grid's pager works
 * from: a full page implies another one, so no row count has to be estimated here.
 */
const useCommandHistoryState = ({ commandType }: CommandHistoryDialogProps) => {
  const [pagination, setPagination] = useState<PaginationParams>({ offset: 0, limit: DEFAULT_PAGE_SIZE })
  const { data, isLoading, isError } = usePagedCommandHistory(commandType, pagination.offset, pagination.limit)

  return { pagination, setPagination, rows: data ?? [], isLoading, isError }
}

type CommandHistoryState = ReturnType<typeof useCommandHistoryState>

const CommandHistoryContent = ({ state }: { state: CommandHistoryState }) => {
  const { t } = useTranslation()
  const columns = useCommandHistoryColumns({ includeErrorMessage: true })

  if (state.isError) {
    return <Banner severity="error">{t('admin.devTools.history.loadError')}</Banner>
  }

  return (
    <DataGrid
      rows={state.rows}
      columns={columns}
      loading={state.isLoading}
      pagination={state.pagination}
      onPaginationChange={state.setPagination}
      canChangePageSize
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      dense
    />
  )
}

/** One handle every command card opens with its own command type and title. */
export const useCommandHistoryDialog = () => {
  const { t } = useTranslation()

  return useDialog<CommandHistoryDialogProps, void, CommandHistoryState>({
    title: (ctx) => t('admin.devTools.history.dialogTitle', { commandTitle: ctx.props.commandTitle }),
    maxWidth: 'lg',
    fullWidth: true,
    hideConfirm: true,
    useDialogState: useCommandHistoryState,
    content: (ctx) => <CommandHistoryContent state={ctx.state} />,
  })
}
